import { useEffect } from 'react'

/**
 * Hold the screen awake while `active`.
 *
 * A toddler stops to think for thirty seconds and the screen dims mid-puzzle;
 * to her the activity broke. So Kid Mode holds a wake lock for as long as it
 * is open.
 *
 * The lock is re-acquired on becoming visible again, because the browser
 * silently drops it whenever the tab is backgrounded — without that, the very
 * first interruption is also the last time the lock works.
 *
 * Every failure path is silent by design: Safari on older iOS has no Wake Lock
 * API, and the request is refused outright on low battery. Neither is worth a
 * message to a parent who cannot act on it.
 */
export function useWakeLock(active = true): void {
  useEffect(() => {
    if (!active) return

    const request: ((type: 'screen') => Promise<any>) | undefined =
      (navigator as any)?.wakeLock?.request?.bind((navigator as any).wakeLock)
    if (!request) return

    let sentinel: any = null
    let released = false

    const acquire = async () => {
      if (released || sentinel || document.visibilityState !== 'visible') return
      try {
        const lock = await request('screen')
        if (released) {
          void lock?.release?.()
          return
        }
        sentinel = lock
        // The browser can drop it on its own; forget the handle when it does
        // so the next visibility change asks for a fresh one.
        lock?.addEventListener?.('release', () => {
          if (sentinel === lock) sentinel = null
        })
      } catch {
        /* refused — low battery, or not permitted in this context */
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire()
    }

    void acquire()
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      released = true
      document.removeEventListener('visibilitychange', onVisibility)
      try {
        void sentinel?.release?.()
      } catch {
        /* already gone */
      }
      sentinel = null
    }
  }, [active])
}
